/**
 * FilesPanel Component
 *
 * Project file tree with create/rename/delete + search + sort + file-type
 * icons. 2026-06-27 overhaul (full navigator pass): the context-menu
 * Rename/Delete/New-in-folder handlers — previously dead `// TODO` stubs —
 * are now wired to the projectStore actions (renameFile/deleteFile/createFile)
 * which call the existing backend endpoints. The search box now filters the
 * tree (was decorative). Folders-first alphabetical sort + per-extension icons.
 */
import { useState, useCallback, useRef, useEffect, useMemo, createContext, useContext } from 'react';
import type { ChangeEvent } from 'react';
import {
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  Plus,
  Search,
  MoreHorizontal,
  FilePlus,
  FolderPlus,
  Trash2,
  Edit3,
  Copy,
  Link,
  X,
  Upload,
  FolderUp,
  Brain,
  Download,
} from 'lucide-react';
import { useProjectStore } from '@/stores/projectStore';
import { useBuildStore } from '@/stores/buildStore';
import { useKnowledgeStore } from '@/stores/knowledgeStore';
import { projectService } from '@/services/projectService';
import { isProbablyTextFile, isSafeUploadPath, joinUploadPath, relPathForUpload } from '@/utils/fileUpload';
import type { FileNode, OpenFile } from '@/types';
import clsx from 'clsx';
import { VoiceAgentQuickSwitcher } from './VoiceAgentQuickSwitcher';
import { iconForFile, sortFileTree, filterFileTree, parentDirOf } from './fileTreeUtils';
import { tip } from '@/config/helpContent';

// Get language from filename
function getLanguage(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase();
  const langMap: Record<string, string> = {
    ts: 'typescript',
    tsx: 'typescript',
    js: 'javascript',
    jsx: 'javascript',
    json: 'json',
    css: 'css',
    scss: 'scss',
    html: 'html',
    md: 'markdown',
    py: 'python',
    yaml: 'yaml',
    yml: 'yaml',
  };
  return langMap[ext || ''] || 'plaintext';
}

// --- Tree interaction context (avoids prop-drilling through recursion) ------

interface TreeCtx {
  forceExpand: boolean;
  onFileClick: (node: FileNode) => void;
  onContextMenu: (e: React.MouseEvent, node: FileNode) => void;
  // inline rename
  renamingId: string | null;
  renamingName: string;
  renamingError: string | null;
  setRenamingName: (v: string) => void;
  submitRename: (node: FileNode) => void;
  cancelRename: () => void;
  // inline create-in-folder
  folderCreate: { parentPath: string; type: 'file' | 'directory' } | null;
  folderCreateName: string;
  folderCreateError: string | null;
  setFolderCreateName: (v: string) => void;
  submitFolderCreate: () => void;
  cancelFolderCreate: () => void;
  // drag-to-move
  draggedId: string | null;
  dragOverId: string | null;
  onDragStart: (node: FileNode) => void;
  onDragEnd: () => void;
  onDragOverNode: (node: FileNode) => void;
  onDropOnNode: (node: FileNode) => void;
  // OS-file drop onto a folder row → upload INTO that folder (2026-07-11).
  onExternalFilesDropToFolder: (node: FileNode, files: FileList) => void;
  // multi-select + keyboard nav
  selectedIds: Set<string>;
  onRowClick: (node: FileNode, e: React.MouseEvent) => void;
  onRowKeyDown: (node: FileNode, e: React.KeyboardEvent<HTMLElement>) => void;
}

const TreeContext = createContext<TreeCtx | null>(null);
function useTree(): TreeCtx {
  const ctx = useContext(TreeContext);
  if (!ctx) throw new Error('TreeContext missing');
  return ctx;
}

interface FileTreeNodeProps {
  node: FileNode;
  depth: number;
}

function FileTreeNode({ node, depth }: FileTreeNodeProps) {
  const expandedFolders = useProjectStore((s) => s.expandedFolders);
  const t = useTree();
  const isFolder = node.type === 'directory';
  const isExpanded = t.forceExpand || expandedFolders.has(node.id);
  const isRenaming = t.renamingId === node.id;
  const Icon = isFolder ? (isExpanded ? FolderOpen : Folder) : iconForFile(node.name);
  const renameRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isRenaming && renameRef.current) {
      renameRef.current.focus();
      renameRef.current.select();
    }
  }, [isRenaming]);

  const isSelected = t.selectedIds.has(node.id);
  const showFolderCreate = isFolder && t.folderCreate?.parentPath === node.path;

  return (
    <div>
      <div
        data-testid={`file-row-${node.id}`}
        tabIndex={0}
        role="treeitem"
        aria-selected={isSelected}
        data-selected={isSelected ? 'true' : undefined}
        draggable={!isRenaming}
        onDragStart={(e) => { e.stopPropagation(); t.onDragStart(node); }}
        onDragEnd={t.onDragEnd}
        onDragOver={isFolder ? (e) => { e.preventDefault(); t.onDragOverNode(node); } : undefined}
        onDrop={isFolder ? (e) => {
          e.preventDefault();
          // OS-file drop (carries the 'Files' type) → upload into this folder;
          // an internal drag-to-move carries no such type. stopPropagation so
          // the panel-level root uploader doesn't ALSO handle the same files.
          if (e.dataTransfer?.types?.includes?.('Files')) {
            e.stopPropagation();
            if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
              t.onExternalFilesDropToFolder(node, e.dataTransfer.files);
            }
          } else {
            t.onDropOnNode(node);
          }
        } : undefined}
        className={clsx(
          'flex items-center gap-1 px-2 py-1 cursor-pointer hover:bg-vibe-bg-tertiary rounded group outline-none focus:ring-1 focus:ring-vibe-accent',
          'text-sm text-vibe-text',
          isSelected && 'bg-vibe-accent/15',
          isFolder && t.dragOverId === node.id && 'ring-1 ring-vibe-accent bg-vibe-bg-tertiary'
        )}
        style={{ paddingLeft: `${depth * 12 + 8}px` }}
        onClick={(e) => { if (!isRenaming) t.onRowClick(node, e); }}
        onKeyDown={(e) => { if (!isRenaming) t.onRowKeyDown(node, e); }}
        onContextMenu={(e) => t.onContextMenu(e, node)}
      >
        {/* Expand/Collapse for folders */}
        {isFolder ? (
          <span className="w-4 h-4 flex items-center justify-center">
            {isExpanded ? (
              <ChevronDown className="w-3 h-3 text-vibe-text-secondary" />
            ) : (
              <ChevronRight className="w-3 h-3 text-vibe-text-secondary" />
            )}
          </span>
        ) : (
          <span className="w-4" />
        )}

        {/* Icon */}
        <Icon className={clsx('w-4 h-4', isFolder ? 'text-vibe-accent' : 'text-vibe-text-secondary')} />

        {/* Name OR inline rename input */}
        {isRenaming ? (
          <input
            ref={renameRef}
            data-testid="file-rename-input"
            type="text"
            value={t.renamingName}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => t.setRenamingName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                t.submitRename(node);
              } else if (e.key === 'Escape') {
                e.preventDefault();
                t.cancelRename();
              }
            }}
            onBlur={() => t.cancelRename()}
            className="flex-1 min-w-0 bg-vibe-bg text-vibe-text text-sm rounded px-1 py-0.5 outline-none focus:ring-1 focus:ring-vibe-accent"
          />
        ) : (
          <span className="flex-1 truncate">{node.name}</span>
        )}

        {/* Actions (show on hover). On touch hardware hover never fires, so
            force visible via [@media(pointer:coarse)]:opacity-100. */}
        {!isRenaming && (
          <button
            title={tip('filesRowActions')}
            className="opacity-0 group-hover:opacity-100 [@media(pointer:coarse)]:opacity-100 p-1 hover:bg-vibe-border rounded [@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]"
            onClick={(e) => {
              e.stopPropagation();
              t.onContextMenu(e, node);
            }}
          >
            <MoreHorizontal className="w-3 h-3 text-vibe-text-secondary" />
          </button>
        )}
      </div>

      {/* Inline create-in-folder input (rendered as first child row). */}
      {showFolderCreate && (
        <div className="px-2 py-1" style={{ paddingLeft: `${(depth + 1) * 12 + 8}px` }}>
          <input
            autoFocus
            data-testid="file-create-in-folder-input"
            type="text"
            value={t.folderCreateName}
            onChange={(e) => t.setFolderCreateName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                t.submitFolderCreate();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                t.cancelFolderCreate();
              }
            }}
            onBlur={() => { if (t.folderCreateName === '') t.cancelFolderCreate(); }}
            placeholder={t.folderCreate?.type === 'directory' ? 'folder name' : 'file name'}
            className="w-full bg-vibe-bg text-vibe-text text-sm rounded px-2 py-1 outline-none focus:ring-1 focus:ring-vibe-accent"
          />
          {t.folderCreateError && (
            <div data-testid="file-create-in-folder-error" className="text-vibe-error text-xs mt-1">
              {t.folderCreateError}
            </div>
          )}
        </div>
      )}

      {/* Children */}
      {isFolder && isExpanded && node.children && (
        <div>
          {node.children.map((child) => (
            <FileTreeNode key={child.id} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

interface ContextMenuProps {
  x: number;
  y: number;
  node: FileNode;
  onClose: () => void;
  onNewFile: () => void;
  onNewFolder: () => void;
  onUploadHere: () => void;
  onRename: () => void;
  onDelete: () => void;
  onCopyName: () => void;
  onCopyPath: () => void;
  onAddToKnowledge: () => void;
  onDownload: () => void;
}

function ContextMenu({
  x,
  y,
  node,
  onClose,
  onNewFile,
  onNewFolder,
  onUploadHere,
  onRename,
  onDelete,
  onCopyName,
  onCopyPath,
  onAddToKnowledge,
  onDownload,
}: ContextMenuProps) {
  const isFolder = node.type === 'directory';

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40" onClick={onClose} />

      {/* Menu */}
      <div
        data-testid="file-context-menu"
        className="fixed z-50 bg-vibe-bg-secondary border border-vibe-border rounded-lg shadow-lg py-1 min-w-[160px]"
        style={{ left: x, top: y }}
      >
        {isFolder && (
          <>
            <button
              onClick={onNewFile}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-vibe-text hover:bg-vibe-bg-tertiary"
            >
              <FilePlus className="w-4 h-4" />
              New File
            </button>
            <button
              onClick={onNewFolder}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-vibe-text hover:bg-vibe-bg-tertiary"
            >
              <FolderPlus className="w-4 h-4" />
              New Folder
            </button>
            <button
              data-testid="file-context-upload"
              onClick={onUploadHere}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-vibe-text hover:bg-vibe-bg-tertiary"
            >
              <Upload className="w-4 h-4" />
              Upload files here
            </button>
            <div className="border-t border-vibe-border my-1" />
          </>
        )}
        <button
          onClick={onCopyName}
          className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-vibe-text hover:bg-vibe-bg-tertiary"
        >
          <Copy className="w-4 h-4" />
          Copy filename
        </button>
        <button
          onClick={onCopyPath}
          className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-vibe-text hover:bg-vibe-bg-tertiary"
        >
          <Link className="w-4 h-4" />
          Copy path
        </button>
        {!isFolder && (
          <>
            <div className="border-t border-vibe-border my-1" />
            <button
              data-testid="file-context-download"
              onClick={onDownload}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-vibe-text hover:bg-vibe-bg-tertiary"
            >
              <Download className="w-4 h-4" />
              Download
            </button>
            <button
              data-testid="file-context-add-to-knowledge"
              onClick={onAddToKnowledge}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-vibe-text hover:bg-vibe-bg-tertiary"
            >
              <Brain className="w-4 h-4" />
              Add to knowledge base
            </button>
          </>
        )}
        <div className="border-t border-vibe-border my-1" />
        <button
          onClick={onRename}
          className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-vibe-text hover:bg-vibe-bg-tertiary"
        >
          <Edit3 className="w-4 h-4" />
          Rename
        </button>
        <button
          onClick={onDelete}
          className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-vibe-error hover:bg-vibe-bg-tertiary"
        >
          <Trash2 className="w-4 h-4" />
          Delete
        </button>
      </div>
    </>
  );
}

export function FilesPanel() {
  const { fileTree, openFile } = useProjectStore();
  const createProject = useProjectStore((s) => s.createProject);
  const currentProject = useProjectStore((s) => s.currentProject);
  const createFile = useProjectStore((s) => s.createFile);
  const renameFile = useProjectStore((s) => s.renameFile);
  const deleteFile = useProjectStore((s) => s.deleteFile);
  const expandFolder = useProjectStore((s) => s.expandFolder);
  const toggleFolder = useProjectStore((s) => s.toggleFolder);
  // 2026-06-27 — clicking a .html file makes the preview follow it.
  const previewFile = useBuildStore((s) => s.previewFile);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const treeScrollRef = useRef<HTMLDivElement | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    node: FileNode;
  } | null>(null);
  // W2 (2026-05-25) — root-level inline create-file/folder input state.
  // D-W2-1 LOCKED: inline row at top of tree, NOT modal.
  const [creatingType, setCreatingType] = useState<'file' | 'directory' | null>(null);
  const [creatingName, setCreatingName] = useState('');
  const [creatingError, setCreatingError] = useState<string | null>(null);
  const createInputRef = useRef<HTMLInputElement | null>(null);

  // 2026-06-27 overhaul — inline rename state.
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renamingName, setRenamingName] = useState('');
  const [renamingError, setRenamingError] = useState<string | null>(null);
  // create-inside-a-folder state (distinct from the locked root-create above).
  const [folderCreate, setFolderCreate] = useState<{ parentPath: string; type: 'file' | 'directory' } | null>(null);
  const [folderCreateName, setFolderCreateName] = useState('');
  const [folderCreateError, setFolderCreateError] = useState<string | null>(null);
  // drag-to-move state.
  const [dragged, setDragged] = useState<{ id: string; path: string; name: string; type: 'file' | 'directory' } | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  // 2026-06-29 — Explorer uploader (multi-file picker, recursive folder picker,
  // drag-drop of files from the OS). Writes into the active project root,
  // preserving folder structure from webkitRelativePath.
  const uploadFiles = useProjectStore((s) => s.uploadFiles);
  const uploadBinaryFiles = useProjectStore((s) => s.uploadBinaryFiles);
  const uploadFilesInputRef = useRef<HTMLInputElement | null>(null);
  const uploadFolderInputRef = useRef<HTMLInputElement | null>(null);
  // 2026-07-11 follow-up — "Upload files here" on a folder's context menu. The
  // clicked folder's path is stashed in a ref (read inside the input's onChange
  // callback, so a ref avoids a stale-closure re-render dance) and the picked
  // files upload INTO that folder.
  const contextUploadInputRef = useRef<HTMLInputElement | null>(null);
  const pendingUploadDirRef = useRef<string>('');
  const [uploadStatus, setUploadStatus] = useState<{ busy: boolean; msg: string | null }>({
    busy: false,
    msg: null,
  });
  const [uploadDragOver, setUploadDragOver] = useState(false);

  const processUpload = useCallback(
    async (fileList: FileList | null, targetDir = '') => {
      if (!currentProject) {
        setUploadStatus({ busy: false, msg: 'No active project — open one first.' });
        return;
      }
      const all = fileList ? Array.from(fileList) : [];
      if (all.length === 0) return;
      // 2026-07-11 — binaries are no longer skipped: text files go through the
      // UTF-8 PUT path (Forgejo-synced), binaries POST raw bytes via the
      // multipart upload endpoint. Only genuinely-unsafe paths are skipped.
      // targetDir (2026-07-11 follow-up) prefixes every path when the user
      // uploads INTO a specific folder (right-click "Upload files here" or an
      // OS-file drop onto a folder row); '' = project root (unchanged).
      const skipped: string[] = [];
      const textPrepared: Array<{ path: string; content: string }> = [];
      const binaryPrepared: Array<{ path: string; file: File }> = [];
      for (const f of all) {
        const path = joinUploadPath(targetDir, relPathForUpload(f));
        if (!isSafeUploadPath(path)) {
          skipped.push(path);
          continue;
        }
        if (isProbablyTextFile(f.name)) {
          try {
            textPrepared.push({ path, content: await f.text() });
          } catch {
            // A "text" file that won't decode → upload as raw bytes instead.
            binaryPrepared.push({ path, file: f });
          }
        } else {
          binaryPrepared.push({ path, file: f });
        }
      }
      const total = textPrepared.length + binaryPrepared.length;
      if (total === 0) {
        setUploadStatus({
          busy: false,
          msg: skipped.length > 0 ? `Skipped ${skipped.length} unsafe path(s)` : 'Nothing to upload',
        });
        return;
      }
      setUploadStatus({ busy: true, msg: `Uploading ${total} file(s)…` });
      let written = 0;
      const failures: Array<{ path: string; error: string }> = [];
      if (textPrepared.length > 0) {
        const res = await uploadFiles(currentProject.id, textPrepared);
        written += res.written;
        failures.push(...res.failed);
      }
      if (binaryPrepared.length > 0) {
        const res = await uploadBinaryFiles(currentProject.id, binaryPrepared);
        written += res.written;
        failures.push(...res.failed);
      }
      const parts = [`Uploaded ${written} file(s)`];
      if (failures.length > 0) parts.push(`${failures.length} failed`);
      if (skipped.length > 0) parts.push(`${skipped.length} unsafe skipped`);
      setUploadStatus({ busy: false, msg: parts.join(' · ') });
    },
    [currentProject, uploadFiles, uploadBinaryFiles],
  );

  const onUploadInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      void processUpload(e.target.files);
      // Reset so re-selecting the same file/folder fires change again.
      e.target.value = '';
    },
    [processUpload],
  );

  // 2026-07-11 follow-up — folder-targeted upload: (a) "Upload files here" on a
  // folder's context menu opens the picker with that folder stashed as target;
  // (b) an OS-file drop onto a folder row uploads into it. Both prefix the
  // uploaded paths with the folder via processUpload's targetDir.
  const beginFolderUpload = useCallback((node: FileNode) => {
    closeContextMenu();
    if (node.type !== 'directory') return;
    pendingUploadDirRef.current = node.path;
    contextUploadInputRef.current?.click();
  }, []);

  const onContextUploadChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      void processUpload(e.target.files, pendingUploadDirRef.current);
      pendingUploadDirRef.current = '';
      e.target.value = '';
    },
    [processUpload],
  );

  const handleExternalFilesDropToFolder = useCallback(
    (node: FileNode, files: FileList) => {
      if (node.type !== 'directory') return;
      void processUpload(files, node.path);
    },
    [processUpload],
  );

  // 2026-06-29 — right-click "Add to knowledge base": read the file's content and
  // ingest it into THE MIND (the primary searchable corpus) via the same
  // knowledge-store path the Knowledge tab uses. Best-effort + status line.
  const [knowledgeStatus, setKnowledgeStatus] = useState<{ busy: boolean; msg: string | null }>({
    busy: false,
    msg: null,
  });

  const handleAddToKnowledge = useCallback(
    async (node: FileNode) => {
      if (!currentProject || node.type !== 'file') return;
      const rel = node.path.replace(/^\/+/, '');
      setKnowledgeStatus({ busy: true, msg: `Adding ${node.name} to knowledge base…` });
      try {
        const file = await projectService.readFile(currentProject.id, node.path);
        await useKnowledgeStore.getState().runIngest({
          content: file.content,
          source_name: rel,
          target_services: ['mind'],
          collection: 'docs',
          content_type: node.name.toLowerCase().endsWith('.md') ? 'markdown' : 'text',
        });
        const err = useKnowledgeStore.getState().ingestError;
        setKnowledgeStatus({
          busy: false,
          msg: err ? `Failed: ${err.message}` : `Added ${node.name} to the knowledge base (THE MIND)`,
        });
      } catch (e) {
        setKnowledgeStatus({
          busy: false,
          msg: `Failed: ${e instanceof Error ? e.message : String(e)}`,
        });
      }
    },
    [currentProject],
  );

  // 2026-07-11 — right-click "Download": stream the file's raw bytes to the
  // user's machine (any type, incl. binaries the text editor can't open). The
  // browser's own download UI is the success feedback; only failures surface a
  // status line (silent failure would be dishonest).
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = useCallback(
    async (node: FileNode) => {
      if (!currentProject || node.type !== 'file') return;
      setDownloadError(null);
      try {
        await projectService.downloadFile(currentProject.id, node.path);
      } catch (e) {
        setDownloadError(`Download failed: ${e instanceof Error ? e.message : String(e)}`);
      }
    },
    [currentProject],
  );

  // Focus the root-create input when it appears.
  useEffect(() => {
    if (creatingType !== null && createInputRef.current) {
      createInputRef.current.focus();
    }
  }, [creatingType]);

  const cancelCreate = useCallback(() => {
    setCreatingType(null);
    setCreatingName('');
    setCreatingError(null);
  }, []);

  const submitCreate = useCallback(async () => {
    const trimmed = creatingName.trim();
    if (!trimmed) {
      setCreatingError('name is required');
      return;
    }
    if (!currentProject) {
      setCreatingError('no active project');
      return;
    }
    const submittedType = creatingType === 'directory' ? 'directory' : 'file';
    const result = await createFile(currentProject.id, trimmed, submittedType);
    // R2 C-2 fix: re-check creatingType after await — Esc may have fired
    // synchronously while createFile was in-flight.
    if (creatingType === null) return;
    if (!result.ok) {
      setCreatingError(result.error ?? 'create failed');
      return;
    }
    cancelCreate();
  }, [creatingName, creatingType, currentProject, createFile, cancelCreate]);

  const onCreateKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void submitCreate();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelCreate();
    }
  }, [submitCreate, cancelCreate]);

  // Dismiss on blur ONLY if input is empty (R2 C-1 invariant: typed text is
  // NEVER lost on incidental click-away).
  const handleCreateBlur = useCallback(() => {
    if (creatingName === '') {
      cancelCreate();
    }
  }, [creatingName, cancelCreate]);

  // G1 (2026-08-17): per-file in-flight read guard — a second click on the
  // same not-yet-loaded file must not fire a duplicate GET /read.
  const alreadyFetching = useRef<Set<string>>(new Set());

  const handleFileClick = useCallback(
    (node: FileNode) => {
      if (node.type === 'file') {
        // 2026-08-17 empty-editor fix: the tree inlines content only for a
        // suffix whitelist (+ size cap) — `node.content` is null/undefined for
        // everything else (Dockerfile.*, .sh, .conf, .txt, oversize files).
        // Those used to open as a SILENT EMPTY buffer that Save would write
        // back over the real file. Now: open a read-only 'unfetched'
        // placeholder and fetch the real bytes via GET /api/files/{id}/read.
        const hasInline = node.content != null;
        const file: OpenFile = {
          id: node.id,
          name: node.name,
          path: node.path,
          content: node.content ?? '',
          language: getLanguage(node.name),
          isModified: false,
          contentSource: hasInline ? 'inline' : 'unfetched',
        };
        openFile(file);
        // G1 (2026-08-17): fetch only when needed — skip when a read is
        // already in flight ('unfetched') or content already arrived
        // ('fetched'). A prior 'error' DOES re-fetch: clicking the file again
        // is the deliberate retry path.
        const already = useProjectStore
          .getState()
          .openFiles.find((f) => f.id === node.id)?.contentSource;
        const needsFetch =
          !hasInline && (already == null || already === 'unfetched' || already === 'error');
        const inFlight = alreadyFetching.current.has(node.id);
        if (needsFetch && !inFlight && currentProject) {
          alreadyFetching.current.add(node.id);
          const { setFileContentLoaded, setFileContentError } = useProjectStore.getState();
          void projectService
            .readFile(currentProject.id, node.path)
            .then((res) => setFileContentLoaded(node.id, res.content, res.language))
            .catch((e: unknown) =>
              setFileContentError(node.id, e instanceof Error ? e.message : String(e))
            )
            .finally(() => alreadyFetching.current.delete(node.id));
        }
        // Preview follows .html selection (2026-06-27): clicking a standalone
        // HTML demo loads it in the preview pane (served from source, no build).
        // 2026-08-18: .md follows the same rule — opening a markdown file also
        // renders it formatted in the preview pane (themed MarkdownPreviewPane).
        const lowerName = node.name.toLowerCase();
        if ((lowerName.endsWith('.html') || lowerName.endsWith('.md')) && currentProject) {
          previewFile(currentProject.id, node.path.replace(/^\/+/, ''));
        }
      }
    },
    [openFile, currentProject, previewFile]
  );

  const handleContextMenu = useCallback(
    (e: React.MouseEvent, node: FileNode) => {
      e.preventDefault();
      setContextMenu({ x: e.clientX, y: e.clientY, node });
    },
    []
  );

  const closeContextMenu = () => setContextMenu(null);

  // --- inline rename --------------------------------------------------------
  const beginRename = useCallback((node: FileNode) => {
    setRenamingId(node.id);
    setRenamingName(node.name);
    setRenamingError(null);
  }, []);

  const cancelRename = useCallback(() => {
    setRenamingId(null);
    setRenamingName('');
    setRenamingError(null);
  }, []);

  const submitRename = useCallback(async (node: FileNode) => {
    const trimmed = renamingName.trim();
    if (!trimmed || trimmed === node.name) {
      cancelRename();
      return;
    }
    if (!currentProject) return;
    const newPath = `${parentDirOf(node.path)}/${trimmed}`;
    const result = await renameFile(currentProject.id, node.path, newPath);
    if (renamingId === null) return; // cancelled mid-flight
    if (!result.ok) {
      setRenamingError(result.error ?? 'rename failed');
      return;
    }
    cancelRename();
  }, [renamingName, currentProject, renameFile, renamingId, cancelRename]);

  // --- delete (confirm) -----------------------------------------------------
  const handleDelete = useCallback((node: FileNode) => {
    closeContextMenu();
    if (!currentProject) return;
    if (!window.confirm(`Delete ${node.name}?${node.type === 'directory' ? ' This removes everything inside it.' : ''}`)) {
      return;
    }
    void deleteFile(currentProject.id, node.path);
  }, [currentProject, deleteFile]);

  // --- copy filename / path to clipboard (for pasting into chat) ------------
  // Path is project-relative with the leading slash stripped: for a FILE this
  // matches the ```file:<path> chat convention (FileWriteCard uses e.g.
  // `file:src/Hello.tsx`); for a FOLDER it's just a plain reference path. The
  // `?.` guards a non-secure context (navigator.clipboard is undefined over
  // HTTP, short-circuiting the whole chain) — this IMPROVES on the
  // MarkdownRenderer copy-button (MarkdownRenderer.tsx:71), which omits the
  // `?.` and would throw there; the `.catch` swallows a denied write. Both
  // failure modes are silent (no fallback target, no UI state to update).
  const copyToClipboard = useCallback((text: string) => {
    closeContextMenu();
    void navigator.clipboard?.writeText(text).catch(() => {
      /* clipboard unavailable (non-secure context / denied) — silent */
    });
  }, []);

  // --- create inside a folder ----------------------------------------------
  const beginFolderCreate = useCallback((node: FileNode, type: 'file' | 'directory') => {
    closeContextMenu();
    setFolderCreate({ parentPath: node.path, type });
    setFolderCreateName('');
    setFolderCreateError(null);
    // Make sure the folder is open so the inline input is visible.
    expandFolder(node.id);
  }, [expandFolder]);

  const cancelFolderCreate = useCallback(() => {
    setFolderCreate(null);
    setFolderCreateName('');
    setFolderCreateError(null);
  }, []);

  const submitFolderCreate = useCallback(async () => {
    const trimmed = folderCreateName.trim();
    if (!trimmed) {
      setFolderCreateError('name is required');
      return;
    }
    if (!currentProject || !folderCreate) return;
    const rel = folderCreate.parentPath.replace(/^\/+/, '');
    const fullPath = rel ? `${rel}/${trimmed}` : trimmed;
    const result = await createFile(currentProject.id, fullPath, folderCreate.type);
    if (folderCreate === null) return;
    if (!result.ok) {
      setFolderCreateError(result.error ?? 'create failed');
      return;
    }
    cancelFolderCreate();
  }, [folderCreateName, currentProject, folderCreate, createFile, cancelFolderCreate]);

  // --- drag-to-move ---------------------------------------------------------
  const onDragStart = useCallback((node: FileNode) => {
    setDragged({ id: node.id, path: node.path, name: node.name, type: node.type });
  }, []);
  const onDragEnd = useCallback(() => {
    setDragged(null);
    setDragOverId(null);
  }, []);
  const onDragOverNode = useCallback((node: FileNode) => {
    setDragOverId(node.id);
  }, []);
  const onDropOnNode = useCallback((target: FileNode) => {
    setDragOverId(null);
    const src = dragged;
    setDragged(null);
    if (!src || !currentProject || target.type !== 'directory') return;
    // Block dropping onto self, or a folder into its own subtree.
    if (target.id === src.id) return;
    if (src.type === 'directory' && (target.path === src.path || target.path.startsWith(`${src.path}/`))) return;
    const targetDir = target.path === '/' ? '' : target.path;
    // No-op when already in that directory.
    if (parentDirOf(src.path) === targetDir) return;
    const newPath = `${targetDir}/${src.name}`;
    void renameFile(currentProject.id, src.path, newPath);
  }, [dragged, currentProject, renameFile]);

  // --- multi-select + keyboard nav -----------------------------------------
  const flattenTree = useCallback((node: FileNode): FileNode[] =>
    [node, ...((node.children ?? []).flatMap(flattenTree))], []);

  const onRowClick = useCallback((node: FileNode, e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey) {
      // Toggle membership; do NOT open/expand on modifier-click.
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(node.id)) next.delete(node.id);
        else next.add(node.id);
        return next;
      });
      return;
    }
    setSelectedIds(new Set([node.id]));
    if (node.type === 'directory') toggleFolder(node.id);
    else handleFileClick(node);
  }, [toggleFolder, handleFileClick]);

  const focusSiblingRow = useCallback((nodeId: string, dir: 1 | -1) => {
    const root = treeScrollRef.current;
    if (!root) return;
    const rows = Array.from(root.querySelectorAll<HTMLElement>('[data-testid^="file-row-"]'));
    const idx = rows.findIndex((r) => r.getAttribute('data-testid') === `file-row-${nodeId}`);
    if (idx === -1) return;
    rows[idx + dir]?.focus();
  }, []);

  const onRowKeyDown = useCallback((node: FileNode, e: React.KeyboardEvent<HTMLElement>) => {
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); focusSiblingRow(node.id, 1); break;
      case 'ArrowUp': e.preventDefault(); focusSiblingRow(node.id, -1); break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (node.type === 'directory') toggleFolder(node.id);
        else handleFileClick(node);
        break;
      case 'F2':
        e.preventDefault();
        beginRename(node);
        break;
      case 'Delete':
      case 'Backspace': {
        e.preventDefault();
        if (!currentProject || !fileTree) return;
        // Bulk-delete the selection when the focused row is part of it; else
        // just the focused row.
        const ids = selectedIds.size > 0 && selectedIds.has(node.id) ? selectedIds : new Set([node.id]);
        const targets = flattenTree(fileTree).filter((n) => ids.has(n.id));
        if (targets.length === 0) return;
        const label = targets.length === 1 ? targets[0].name : `${targets.length} items`;
        if (!window.confirm(`Delete ${label}?`)) return;
        targets.forEach((n) => void deleteFile(currentProject.id, n.path));
        setSelectedIds(new Set());
        break;
      }
    }
  }, [focusSiblingRow, toggleFolder, handleFileClick, beginRename, currentProject, fileTree, selectedIds, flattenTree, deleteFile]);

  // Sorted (always) + filtered (when searching) view of the tree.
  const displayTree = useMemo(() => {
    if (!fileTree) return null;
    const sorted = sortFileTree(fileTree);
    return searchQuery.trim() ? filterFileTree(sorted, searchQuery) : sorted;
  }, [fileTree, searchQuery]);
  const forceExpand = searchQuery.trim().length > 0;

  const treeCtx: TreeCtx = {
    forceExpand,
    onFileClick: handleFileClick,
    onContextMenu: handleContextMenu,
    renamingId,
    renamingName,
    renamingError,
    setRenamingName,
    submitRename: (node) => void submitRename(node),
    cancelRename,
    folderCreate,
    folderCreateName,
    folderCreateError,
    setFolderCreateName,
    submitFolderCreate: () => void submitFolderCreate(),
    cancelFolderCreate,
    draggedId: dragged?.id ?? null,
    dragOverId,
    onDragStart,
    onDragEnd,
    onDragOverNode,
    onDropOnNode,
    onExternalFilesDropToFolder: handleExternalFilesDropToFolder,
    selectedIds,
    onRowClick,
    onRowKeyDown,
  };

  return (
    <div
      className={`flex flex-col h-full w-full overflow-hidden ${
        uploadDragOver ? 'ring-2 ring-inset ring-vibe-accent' : ''
      }`}
      data-testid="files-panel-root"
      onDragOver={(e) => {
        // Only intercept external file drags (OS files carry the 'Files' type);
        // internal drag-to-move reordering sets no such type (and in jsdom
        // events carry no dataTransfer at all) and is left untouched.
        if (!currentProject || !e.dataTransfer?.types?.includes('Files')) return;
        e.preventDefault();
        if (!uploadDragOver) setUploadDragOver(true);
      }}
      onDragLeave={(e) => {
        if (e.currentTarget === e.target) setUploadDragOver(false);
      }}
      onDrop={(e) => {
        if (!currentProject || !e.dataTransfer?.types?.includes('Files')) return;
        e.preventDefault();
        setUploadDragOver(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
          void processUpload(e.dataTransfer.files);
        }
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-vibe-border">
        <span className="text-sm font-medium text-vibe-text">Explorer</span>
        <div className="flex items-center gap-1">
          <button
            data-testid="files-new-file-button"
            title={tip('filesNewFile')}
            // Browser focus-race fix (2026-05-27): preventDefault keeps focus on
            // the input so onClick runs alone with state intact.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              if (creatingType === 'file') {
                if (creatingName === '') cancelCreate();
                return;
              }
              setCreatingType('file');
              setCreatingName('');
              setCreatingError(null);
            }}
            className="p-1 hover:bg-vibe-bg-tertiary rounded text-vibe-text-secondary hover:text-vibe-text [@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]"
          >
            <FilePlus className="w-4 h-4" />
          </button>
          <button
            data-testid="files-new-folder-button"
            title={tip('filesNewFolder')}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              if (creatingType === 'directory') {
                if (creatingName === '') cancelCreate();
                return;
              }
              setCreatingType('directory');
              setCreatingName('');
              setCreatingError(null);
            }}
            className="p-1 hover:bg-vibe-bg-tertiary rounded text-vibe-text-secondary hover:text-vibe-text [@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]"
          >
            <FolderPlus className="w-4 h-4" />
          </button>
          {/* 2026-06-29 — bulk uploader: multi-file + recursive folder. */}
          <button
            data-testid="files-upload-files-button"
            title="Upload files into the project (multi-select)"
            disabled={!currentProject || uploadStatus.busy}
            onClick={() => uploadFilesInputRef.current?.click()}
            className="p-1 hover:bg-vibe-bg-tertiary rounded text-vibe-text-secondary hover:text-vibe-text disabled:opacity-40 [@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]"
          >
            <Upload className="w-4 h-4" />
          </button>
          <button
            data-testid="files-upload-folder-button"
            title="Upload a folder into the project (keeps its structure)"
            disabled={!currentProject || uploadStatus.busy}
            onClick={() => uploadFolderInputRef.current?.click()}
            className="p-1 hover:bg-vibe-bg-tertiary rounded text-vibe-text-secondary hover:text-vibe-text disabled:opacity-40 [@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]"
          >
            <FolderUp className="w-4 h-4" />
          </button>
          <input
            ref={uploadFilesInputRef}
            data-testid="files-upload-files-input"
            type="file"
            multiple
            className="hidden"
            onChange={onUploadInputChange}
          />
          <input
            ref={uploadFolderInputRef}
            data-testid="files-upload-folder-input"
            type="file"
            multiple
            // webkitdirectory is non-standard but well-supported; it makes the
            // picker select a whole folder and populates webkitRelativePath.
            {...({ webkitdirectory: '' } as Record<string, string>)}
            className="hidden"
            onChange={onUploadInputChange}
          />
          {/* 2026-07-11 — hidden input for the folder context-menu "Upload
              files here"; uploads into the folder stashed in pendingUploadDirRef. */}
          <input
            ref={contextUploadInputRef}
            data-testid="files-context-upload-input"
            type="file"
            multiple
            className="hidden"
            onChange={onContextUploadChange}
          />
        </div>
      </div>
      {uploadStatus.msg && (
        <div
          data-testid="files-upload-status"
          className="px-3 py-1.5 text-xs border-b border-vibe-border text-vibe-text-secondary bg-vibe-bg-tertiary"
        >
          {uploadStatus.msg}
        </div>
      )}
      {knowledgeStatus.msg && (
        <div
          data-testid="files-knowledge-status"
          className="px-3 py-1.5 text-xs border-b border-vibe-border text-vibe-text-secondary bg-vibe-bg-tertiary"
        >
          {knowledgeStatus.msg}
        </div>
      )}
      {downloadError && (
        <div
          data-testid="files-download-error"
          className="px-3 py-1.5 text-xs border-b border-vibe-border text-vibe-error bg-vibe-bg-tertiary"
        >
          {downloadError}
        </div>
      )}

      {/* W2 — root inline create-file/folder input row. */}
      {creatingType !== null && (
        <div className="px-3 py-2 border-b border-vibe-border bg-vibe-bg-tertiary">
          <div className="flex items-center gap-1">
            <input
              ref={createInputRef}
              data-testid="files-new-file-input"
              type="text"
              value={creatingName}
              onChange={(e) => {
                setCreatingName(e.target.value);
                if (creatingError) setCreatingError(null);
              }}
              onKeyDown={onCreateKeyDown}
              onBlur={handleCreateBlur}
              placeholder={creatingType === 'directory' ? 'folder name' : 'file name'}
              className="flex-1 bg-vibe-bg text-vibe-text text-sm rounded px-2 py-1 outline-none focus:ring-1 focus:ring-vibe-accent"
            />
            <button
              data-testid="files-cancel-create-button"
              onClick={cancelCreate}
              aria-label="Cancel"
              className="p-1 hover:bg-vibe-bg rounded text-vibe-text-secondary hover:text-vibe-text [@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {creatingError && (
            <div data-testid="files-create-error" className="text-vibe-error text-xs mt-1">
              {creatingError}
            </div>
          )}
        </div>
      )}

      {/* Search */}
      <div className="p-2 border-b border-vibe-border">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-vibe-text-secondary" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search files..."
            className="w-full bg-vibe-bg text-vibe-text text-sm rounded pl-8 pr-3 py-1.5 outline-none focus:ring-1 focus:ring-vibe-accent"
          />
        </div>
      </div>

      {/* File Tree */}
      <div ref={treeScrollRef} className="flex-1 overflow-auto py-2">
        {displayTree ? (
          <TreeContext.Provider value={treeCtx}>
            <FileTreeNode node={displayTree} depth={0} />
          </TreeContext.Provider>
        ) : fileTree ? (
          // A tree exists but the search filtered everything out.
          <div className="flex flex-col items-center justify-center h-full text-vibe-text-secondary p-4">
            <Search className="w-8 h-8 opacity-50 mb-2" />
            <p className="text-sm text-center">No files match “{searchQuery.trim()}”</p>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-vibe-text-secondary p-4">
            <Folder className="w-12 h-12 opacity-50 mb-3" />
            <p className="text-sm text-center">No project loaded</p>
            <button
              data-testid="new-project-button"
              onClick={() => void createProject('My New Project')}
              className="mt-3 flex items-center gap-2 px-3 py-1.5 text-sm bg-vibe-accent hover:bg-vibe-accent-hover text-white rounded"
            >
              <Plus className="w-4 h-4" />
              New Project
            </button>
          </div>
        )}
      </div>

      {/* Voice Agent quick switcher (F.2). */}
      <VoiceAgentQuickSwitcher />

      {/* Context Menu — handlers wired 2026-06-27 (were dead // TODO stubs). */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          node={contextMenu.node}
          onClose={closeContextMenu}
          onNewFile={() => beginFolderCreate(contextMenu.node, 'file')}
          onNewFolder={() => beginFolderCreate(contextMenu.node, 'directory')}
          onUploadHere={() => beginFolderUpload(contextMenu.node)}
          onRename={() => {
            beginRename(contextMenu.node);
            closeContextMenu();
          }}
          onDelete={() => handleDelete(contextMenu.node)}
          onCopyName={() => copyToClipboard(contextMenu.node.name)}
          onCopyPath={() => copyToClipboard(contextMenu.node.path.replace(/^\/+/, ''))}
          onDownload={() => {
            const n = contextMenu.node;
            closeContextMenu();
            void handleDownload(n);
          }}
          onAddToKnowledge={() => {
            const n = contextMenu.node;
            closeContextMenu();
            void handleAddToKnowledge(n);
          }}
        />
      )}
    </div>
  );
}
